import React, {
  useEffect,
  useMemo,
  useState,
} from "react";

import ReactDOM from "react-dom";

import {
  FaTimes,
  FaUserShield,
} from "react-icons/fa";

import API from "../../../../../utils/apiCalling";
import { config } from "../../../../../utils/apiUrl";

import "./ChangeStatusDialog.css";

const STATUS_OPTIONS = [
  {
    value: "ACTIVE",
    label: "Active",
    description:
      "Customer can log in and place orders.",
  },
  {
    value: "INACTIVE",
    label: "Inactive",
    description:
      "Customer login is temporarily disabled.",
  },
  {
    value: "BLOCKED",
    label: "Blocked",
    description:
      "Customer access is completely blocked.",
  },
];

const ChangeStatusDialog = ({
  open,
  onClose,
  user,
  onSuccess,
}) => {
  const api = useMemo(
    () => new API({tokenKey: "platformAccessToken"}),
    []
  );

  const [status, setStatus] =
    useState("ACTIVE");

  const [loading, setLoading] =
    useState(false);

  const [error, setError] =
    useState("");

  useEffect(() => {
    if (!open || !user) {
      return;
    }

    setStatus(
      String(
        user?.status || "ACTIVE"
      ).toUpperCase()
    );

    setError("");
  }, [open, user]);

  useEffect(() => {
    if (!open) {
      return undefined;
    }

    const previousOverflow =
      document.body.style.overflow;

    document.body.style.overflow =
      "hidden";

    return () => {
      document.body.style.overflow =
        previousOverflow;
    };
  }, [open]);

  const handleSubmit = async () => {
    try {
      setLoading(true);
      setError("");

      const response =
        await api.patch(
          config.changeUserStatus(
            user._id
          ),
          {
            status,
          }
        );

      if (
        response?.status !==
        "success"
      ) {
        throw new Error(
          response?.message ||
            "Unable to update status."
        );
      }

      await onSuccess?.(
        response?.data
      );
    } catch (requestError) {
      setError(
        requestError?.response?.data
          ?.message ||
          requestError?.message ||
          "Unable to update status."
      );
    } finally {
      setLoading(false);
    }
  };

  if (!open || !user) {
    return null;
  }

  return ReactDOM.createPortal(
    <div className="customer-status-modal-root">
      <button
        type="button"
        className="customer-status-backdrop"
        onClick={onClose}
        aria-label="Close status dialog"
      />

      <section
        className="customer-status-dialog"
        role="dialog"
        aria-modal="true"
      >
        <header className="customer-status-header">
          <div>
            <FaUserShield />

            <div>
              <small>
                CUSTOMER MANAGEMENT
              </small>

              <h2>
                Change Status
              </h2>
            </div>
          </div>

          <button
            type="button"
            onClick={onClose}
            disabled={loading}
            aria-label="Close"
          >
            <FaTimes />
          </button>
        </header>

        <div className="customer-status-body">
          <p>
            Change account status for
          </p>

          <h3>
            {user?.name ||
              user?.fullName ||
              user?.mobile}
          </h3>

          {error ? (
            <div className="customer-status-error">
              {error}
            </div>
          ) : null}

          <div className="customer-status-options">
            {STATUS_OPTIONS.map(
              (option) => (
                <label
                  key={option.value}
                  className={
                    status ===
                    option.value
                      ? "selected"
                      : ""
                  }
                >
                  <input
                    type="radio"
                    name="status"
                    value={
                      option.value
                    }
                    checked={
                      status ===
                      option.value
                    }
                    onChange={(
                      event
                    ) =>
                      setStatus(
                        event.target
                          .value
                      )
                    }
                    disabled={loading}
                  />

                  <div>
                    <strong>
                      {option.label}
                    </strong>

                    <span>
                      {
                        option.description
                      }
                    </span>
                  </div>
                </label>
              )
            )}
          </div>
        </div>

        <footer className="customer-status-footer">
          <button
            type="button"
            className="customer-status-cancel"
            onClick={onClose}
            disabled={loading}
          >
            Cancel
          </button>

          <button
            type="button"
            className="customer-status-save"
            onClick={handleSubmit}
            disabled={
              loading ||
              status ===
                String(
                  user?.status ||
                    ""
                ).toUpperCase()
            }
          >
            {loading
              ? "Updating..."
              : "Update Status"}
          </button>
        </footer>
      </section>
    </div>,
    document.body
  );
};

export default ChangeStatusDialog;